/**
 * Sample weather verdicts.
 *
 * Four cases, because the tri-state plus the reason-for-refusal is the whole
 * module. The one that matters is the surface-temperature refusal: the air
 * temperature is on file and looks perfect, and the answer is still UNKNOWN,
 * because asphalt in afternoon sun sits well above the air and substituting
 * the reading that happens to be available is how an engineer learns to
 * override the system.
 *
 *   crack-seal   unknown: the limit is on the substrate, only air is on file
 *   line-marking blocked by wind — a material limit, arguable
 *   sweeping     allowed, on an observation
 *   any other    no weather data at all
 */
import type { CanWork } from "../domain/weather";

export function mockCanWork(category: string, at: string): CanWork {
  if (category === "crack-seal") {
    return {
      category,
      at,
      conditions: {
        known: true,
        basis: "observation taken 22 minutes ago",
        airTempC: 31,
        windKt: 8,
        humidity: 0.24,
      },
      breaches: [],
      unknowns: [
        {
          kind: "material",
          why:
            "the limit is on SUBSTRATE temperature (10–50 °C) and only an air temperature is on file. " +
            "Asphalt in afternoon sun sits 20–30 °C above the air, so this sealant can fail at either " +
            "end on a day the air temperature calls perfect.",
        },
      ],
      limitsOnFile: 2,
      checksMade: 1,
      known: false,
      /* not false — nobody has taken the reading */
      allowed: null,
      verdict:
        "1 limit could not be evaluated. This is not permission to work — it is a reading somebody needs " +
        "to take.",
    };
  }

  if (category === "line-marking") {
    return {
      category,
      at,
      conditions: {
        known: true,
        basis: "observation taken 8 minutes ago",
        airTempC: 29,
        windKt: 27,
        gustKt: 34,
      },
      breaches: [
        { kind: "material", why: "wind 27 kt against a 20 kt limit — paint will not lay evenly" },
        { kind: "regulatory", why: "midday summer work ban, 11:00 to 15:00 local" },
      ],
      unknowns: [],
      limitsOnFile: 3,
      checksMade: 3,
      known: true,
      allowed: false,
      verdict:
        "2 limits are breached. The wind is arguable — wait for it to drop. The work ban is not: it is a " +
        "clock rule, and a cool day does not lift it.",
    };
  }

  if (category === "sweeping") {
    return {
      category,
      at,
      conditions: {
        known: true,
        basis: "observation taken 5 minutes ago",
        airTempC: 27,
        windKt: 9,
        visibilityM: 9000,
      },
      breaches: [],
      unknowns: [],
      limitsOnFile: 2,
      checksMade: 2,
      known: true,
      allowed: true,
      verdict: "Both limits on file were evaluated and neither is breached.",
    };
  }

  return {
    category,
    at,
    conditions: {
      known: false,
      whyNot:
        "no observation within 90 minutes and no forecast covering this time. That is missing data, not " +
        "good weather.",
    },
    breaches: [],
    unknowns: [
      { kind: "material", why: "no weather data for this time at all" },
      { kind: "regulatory", why: "no weather data for this time at all" },
    ],
    limitsOnFile: 2,
    checksMade: 0,
    known: false,
    allowed: null,
    verdict:
      "2 limits could not be evaluated. This is not permission to work — it is a list of readings " +
      "somebody needs to take.",
  };
}
